/**
 * GUMMY LIFE Theme JavaScript
 * Bienestar que se disfruta
 */

(function() {
  'use strict';

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================

  const debounce = (fn, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn(...args), delay);
    };
  };

  const formatMoney = (cents, format = '${{amount}} MXN') => {
    const value = (cents / 100).toFixed(2);
    return format.replace('{{amount}}', value);
  };

  const fetchJSON = async (url, options = {}) => {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      }
    });
    return response.json();
  };

  // ============================================
  // CART FUNCTIONALITY
  // ============================================

  const Cart = {
    async add(variantId, quantity = 1) {
      try {
        const response = await fetchJSON(window.gummyLife.routes.cart_add_url + '.js', {
          method: 'POST',
          body: JSON.stringify({
            id: variantId,
            quantity: quantity
          })
        });

        this.updateCartCount();
        this.openDrawer();
        return response;
      } catch (error) {
        console.error('Error adding to cart:', error);
        throw error;
      }
    },

    async update(lineKey, quantity) {
      try {
        const response = await fetchJSON(window.gummyLife.routes.cart_change_url + '.js', {
          method: 'POST',
          body: JSON.stringify({
            id: lineKey,
            quantity: quantity
          })
        });

        this.updateCartCount();
        return response;
      } catch (error) {
        console.error('Error updating cart:', error);
        throw error;
      }
    },

    async remove(lineKey) {
      return this.update(lineKey, 0);
    },

    async getCart() {
      return fetchJSON('/cart.js');
    },

    async updateCartCount() {
      const cart = await this.getCart();
      const countElements = document.querySelectorAll('[data-cart-count]');
      countElements.forEach(el => {
        el.textContent = cart.item_count;
      });
    },

    openDrawer() {
      const drawer = document.querySelector('[data-cart-drawer]');
      const overlay = document.querySelector('[data-cart-overlay]');

      if (drawer && overlay) {
        drawer.classList.add('open');
        overlay.classList.add('open');
        document.body.style.overflow = 'hidden';
      }
    },

    closeDrawer() {
      const drawer = document.querySelector('[data-cart-drawer]');
      const overlay = document.querySelector('[data-cart-overlay]');

      if (drawer && overlay) {
        drawer.classList.remove('open');
        overlay.classList.remove('open');
        document.body.style.overflow = '';
      }
    },

    init() {
      // Cart toggle
      const cartToggle = document.querySelector('[data-cart-toggle]');
      cartToggle?.addEventListener('click', () => this.openDrawer());

      // Cart close
      const closeButtons = document.querySelectorAll('[data-cart-close]');
      closeButtons.forEach(btn => btn.addEventListener('click', () => this.closeDrawer()));

      // Cart overlay
      const overlay = document.querySelector('[data-cart-overlay]');
      overlay?.addEventListener('click', () => this.closeDrawer());

      // Quick add buttons are handled by cart-drawer.liquid via event delegation
    }
  };

  // ============================================
  // MOBILE MENU
  // ============================================

  const MobileMenu = {
    init() {
      const toggle = document.querySelector('[data-mobile-menu-toggle]');
      const menu = document.querySelector('[data-mobile-menu]');
      const close = document.querySelector('[data-mobile-menu-close]');

      toggle?.addEventListener('click', () => {
        menu?.classList.add('open');
        document.body.style.overflow = 'hidden';
      });

      close?.addEventListener('click', () => {
        menu?.classList.remove('open');
        document.body.style.overflow = '';
      });
    }
  };

  // ============================================
  // STICKY HEADER
  // ============================================

  const StickyHeader = {
    init() {
      const header = document.querySelector('[data-header]');
      if (!header) return;

      let lastScroll = 0;
      let ticking = false;

      const updateHeader = () => {
        const currentScroll = window.pageYOffset;

        if (currentScroll > 10) {
          header.classList.add('scrolled');
        } else {
          header.classList.remove('scrolled');
        }

        lastScroll = currentScroll;
        ticking = false;
      };

      window.addEventListener('scroll', () => {
        if (!ticking) {
          requestAnimationFrame(updateHeader);
          ticking = true;
        }
      }, { passive: true });
    }
  };

  // ============================================
  // PRODUCT VARIANTS
  // ============================================

  const ProductVariants = {
    init() {
      const variantButtons = document.querySelectorAll('[data-option-value]');
      const variantInput = document.querySelector('[data-variant-id]');
      const priceElement = document.querySelector('[data-product-price]');

      if (!variantButtons.length) return;

      variantButtons.forEach(btn => {
        btn.addEventListener('click', () => {
          // Update selected state
          const optionName = btn.dataset.optionName;
          document.querySelectorAll(`[data-option-name="${optionName}"]`).forEach(b => {
            b.classList.remove('selected');
          });
          btn.classList.add('selected');

          // Get selected options
          const selectedOptions = [];
          document.querySelectorAll('.product-variant__options').forEach(group => {
            const selected = group.querySelector('.selected');
            if (selected) {
              selectedOptions.push(selected.dataset.optionValue);
            }
          });

          // Find matching variant (you'd need to pass variant data from Liquid)
          // This is a simplified example
          console.log('Selected options:', selectedOptions);
        });
      });
    }
  };

  // ============================================
  // QUANTITY SELECTOR
  // ============================================

  const QuantitySelector = {
    init() {
      document.querySelectorAll('[data-quantity-input]').forEach(input => {
        const wrapper = input.closest('.product-quantity__selector') ||
                       input.closest('.cart-item__quantity');

        if (!wrapper) return;

        const minusBtn = wrapper.querySelector('[data-quantity-minus], [data-quantity-decrease]');
        const plusBtn = wrapper.querySelector('[data-quantity-plus], [data-quantity-increase]');

        minusBtn?.addEventListener('click', () => {
          const currentValue = parseInt(input.value) || 1;
          if (currentValue > 1) {
            input.value = currentValue - 1;
            input.dispatchEvent(new Event('change'));
          }
        });

        plusBtn?.addEventListener('click', () => {
          const currentValue = parseInt(input.value) || 1;
          input.value = currentValue + 1;
          input.dispatchEvent(new Event('change'));
        });
      });
    }
  };

  // ============================================
  // SCROLL ANIMATIONS
  // ============================================

  const ScrollAnimations = {
    init() {
      // Skip animations if user prefers reduced motion
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        document.querySelectorAll('[data-animate]').forEach(el => {
          el.classList.add('animated');
        });
        return;
      }

      const animatedElements = document.querySelectorAll('[data-animate]');
      if (!animatedElements.length) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animated');
            observer.unobserve(entry.target);
          }
        });
      }, {
        threshold: 0.05,
        rootMargin: '0px 0px -10% 0px'
      });

      animatedElements.forEach(el => observer.observe(el));
    }
  };

  // ============================================
  // NEWSLETTER FORM
  // ============================================

  const Newsletter = {
    init() {
      const forms = document.querySelectorAll('form[action*="contact"]');

      forms.forEach(form => {
        form.addEventListener('submit', async (e) => {
          e.preventDefault();

          const submitBtn = form.querySelector('button[type="submit"]');
          const originalText = submitBtn.innerHTML;

          submitBtn.disabled = true;
          submitBtn.innerHTML = '<span class="loading"></span>';

          try {
            const formData = new FormData(form);
            await fetch(form.action, {
              method: 'POST',
              body: formData
            });

            submitBtn.innerHTML = '✓ ' + window.gummyLife?.strings?.subscribed || 'Subscribed!';
            form.reset();

            setTimeout(() => {
              submitBtn.disabled = false;
              submitBtn.innerHTML = originalText;
            }, 3000);
          } catch (error) {
            submitBtn.disabled = false;
            submitBtn.innerHTML = originalText;
            console.error('Newsletter error:', error);
          }
        });
      });
    }
  };

  // ============================================
  // TABS
  // ============================================

  const Tabs = {
    init() {
      const tabButtons = document.querySelectorAll('[data-tab-btn]');

      tabButtons.forEach(btn => {
        btn.addEventListener('click', () => {
          const tabId = btn.dataset.tab;
          const tabGroup = btn.closest('.product-tabs');

          if (!tabGroup) return;

          // Update buttons
          tabGroup.querySelectorAll('[data-tab-btn]').forEach(b => {
            b.classList.remove('active');
          });
          btn.classList.add('active');

          // Update content
          tabGroup.querySelectorAll('[data-tab-content]').forEach(content => {
            content.classList.remove('active');
          });

          const targetContent = tabGroup.querySelector(`[data-tab-content="${tabId}"]`);
          targetContent?.classList.add('active');
        });
      });
    }
  };

  // ============================================
  // GALLERY
  // ============================================

  const Gallery = {
    init() {
      const thumbnails = document.querySelectorAll('[data-thumbnail]');
      const mainImage = document.getElementById('main-product-image');

      if (!thumbnails.length || !mainImage) return;

      thumbnails.forEach(thumb => {
        thumb.addEventListener('click', () => {
          // Update active state
          thumbnails.forEach(t => t.classList.remove('active'));
          thumb.classList.add('active');

          // Update main image
          const newSrc = thumb.dataset.imageSrc;
          mainImage.src = newSrc;
        });
      });
    }
  };

  // ============================================
  // LANGUAGE PREFERENCE
  // ============================================

  const Language = {
    init() {
      // Store language preference
      const languageLinks = document.querySelectorAll('.language-switcher__btn, .language-switcher__btn-block');

      languageLinks.forEach(link => {
        link.addEventListener('click', (e) => {
          const lang = link.getAttribute('hreflang');
          if (lang) {
            localStorage.setItem('gummylife-language', lang);
          }
        });
      });
    }
  };

  // ============================================
  // SUBSCRIPTION TOGGLE
  // ============================================

  const SubscriptionToggle = {
    init() {
      const purchaseOptions = document.querySelectorAll('input[name="purchase_option"]');
      const frequencyDiv = document.querySelector('[data-subscription-frequency]');

      if (!purchaseOptions.length) return;

      purchaseOptions.forEach(input => {
        input.addEventListener('change', () => {
          if (frequencyDiv) {
            frequencyDiv.style.display = input.value === 'subscribe' ? 'block' : 'none';
          }
        });
      });
    }
  };

  // ============================================
  // LAZY LOADING
  // ============================================

  const LazyLoad = {
    init() {
      const lazyImages = document.querySelectorAll('img[loading="lazy"]');

      if ('loading' in HTMLImageElement.prototype) {
        // Native lazy loading supported
        return;
      }

      // Fallback for older browsers
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            img.src = img.dataset.src;
            imageObserver.unobserve(img);
          }
        });
      });

      lazyImages.forEach(img => imageObserver.observe(img));
    }
  };

  // ============================================
  // INITIALIZE ALL MODULES
  // ============================================

  document.addEventListener('DOMContentLoaded', () => {
    Cart.init();
    MobileMenu.init();
    StickyHeader.init();
    ProductVariants.init();
    QuantitySelector.init();
    ScrollAnimations.init();
    Newsletter.init();
    Tabs.init();
    Gallery.init();
    Language.init();
    SubscriptionToggle.init();
    LazyLoad.init();

    console.log('🍬 Gummy Life theme initialized');
  });

  // Expose Cart globally for use in other scripts
  window.GummyLifeCart = Cart;

})();
